
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { topPicks } from '../../../mocks/homepage';

const filterTabs = [
  'All',
  'Marketing Tools',
  'SEO Tools',
  'Web Hosting',
  'Web Design',
  'Business Tools',
  'Creative Tools',
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <i
          key={star}
          className={`ri-star-${
            star <= Math.floor(rating)
              ? 'fill'
              : star - 0.5 <= rating
              ? 'half-fill'
              : 'line'
          } text-neon text-xs`}
        ></i>
      ))}
    </div>
  );
}

export default function TopPicksSection() {
  const [activeTab, setActiveTab] = useState('All');

  const filteredProducts =
    activeTab === 'All'
      ? topPicks
      : topPicks.filter((p) => p.category === activeTab);

  return (
    <section className="py-24 bg-carbon-950">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-10">
          <span className="text-xs font-bold uppercase tracking-widest text-neon mb-3 block">
            Curated for You
          </span>
          <h2 className="text-4xl font-bold text-white mb-4 tracking-tight">Our Top Picks</h2>
          <p className="text-sm text-white/40 max-w-xl mx-auto">
            Hand-picked tools reviewed by our team. Rated by thousands of real users.
          </p>
        </div>

        <div className="flex items-center justify-center gap-1 mb-12 flex-wrap">
          {filterTabs.map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 text-xs font-medium transition-all cursor-pointer whitespace-nowrap border ${
                activeTab === tab
                  ? 'bg-neon text-black border-neon font-bold'
                  : 'bg-transparent text-white/50 border-white/10 hover:border-white/20 hover:text-white/80'
              }`}
            >
              {tab}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4" data-product-shop>
          {filteredProducts.map((product) => (
            <Link
              key={product.id}
              to="/products"
              className="group bg-carbon-900 border border-white/5 overflow-hidden hover:border-neon/20 transition-all duration-300 cursor-pointer"
            >
              <div className="relative w-full h-44 bg-carbon-950 overflow-hidden">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 opacity-80"
                />
                <div className="absolute top-3 left-3 px-2.5 py-1 bg-white/10 backdrop-blur-sm text-white/80 text-xs font-medium whitespace-nowrap border border-white/10">
                  {product.category}
                </div>
              </div>

              <div className="p-5">
                <div className="flex items-center gap-1 px-2 py-1 bg-neon/10 border border-neon/20 w-fit mb-3">
                  <i className="ri-thumb-up-fill text-neon text-xs"></i>
                  <span className="text-xs font-medium text-neon">{product.highlight}</span>
                </div>

                <h3 className="text-sm font-bold text-white mb-2 group-hover:text-neon transition-colors">
                  {product.name}
                </h3>

                <div className="flex items-center gap-2 mb-3">
                  <StarRating rating={product.rating} />
                  <span className="text-xs text-white/30">{product.rating}</span>
                  <span className="text-xs text-white/30">
                    ({product.reviews?.toLocaleString?.() ?? product.reviews})
                  </span>
                </div>

                <div className="flex items-center justify-between pt-3 border-t border-white/5">
                  <span className="text-sm font-bold text-white">{product.price}</span>
                  <span className="text-xs text-neon font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                    View Details <i className="ri-arrow-right-s-line"></i>
                  </span>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="text-center mt-12">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-8 py-3.5 bg-neon text-black text-sm font-bold hover:bg-neon/90 transition-colors cursor-pointer whitespace-nowrap"
          >
            Browse All Products <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
